import React from 'react';

import repeat from 'lodash/repeat';

import { useTranslate } from '@lumapps/translations';

import { PickerMetaData } from '../types';
import { useFetchMetadataFamilyTree, UseFetchMetadataFamilyTreeProps } from './useFetchMetadataFamilyTree';

export interface UseMetadataPickerProps
    extends Pick<UseFetchMetadataFamilyTreeProps, 'params' | 'showMultipleLevels'> {}

/**
 * Hook to manage the Metadata Pickers (simple and multiple).
 *
 * The hook will
 * * Fetch the metadata whole family on select open
 * * Manage the search field and choice filtering
 * * Manage choice name formatting with the correct level
 * * Manage action on choice select
 */
export const useMetadataPicker = ({ params, showMultipleLevels }: UseMetadataPickerProps) => {
    const { translateObject } = useTranslate();
    const [shouldFetch, setFetchEnabled] = React.useState(false);
    const enableFetch = React.useCallback(() => setFetchEnabled(true), []);

    const getMetadataName = React.useCallback(
        (metadata: PickerMetaData) => translateObject(metadata.name),
        [translateObject],
    );
    const [searchText, setSearchText] = React.useState('');

    const { status, metadataList } = useFetchMetadataFamilyTree({
        params,
        shouldFetch,
        showMultipleLevels,
    });

    /** Choices filtered by the current search text */
    const filteredChoices = React.useMemo(
        () =>
            metadataList.filter((metadata: PickerMetaData) => {
                if (metadata.id === params.familyId) {
                    return false;
                }

                if (!searchText) {
                    return true;
                }
                const translatedName = translateObject(metadata.name) || metadata.id;

                return translatedName.trim().toLowerCase().includes(searchText.trim().toLowerCase());
            }),
        [params.familyId, metadataList, searchText, translateObject],
    );

    /** Get the metadata value name with level indicator */
    const getValueName = React.useCallback(
        (metadata: PickerMetaData) => {
            const indentation = repeat('— ', metadata.level || 0);
            const metadataName = translateObject(metadata.name);
            return `${indentation}${metadataName}`;
        },
        [translateObject],
    );

    return {
        status,
        filteredChoices,
        searchText,
        getValueName,
        getMetadataName,
        onSearch: setSearchText,
        onOpen: enableFetch,
    };
};
